import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Button from '@mui/material/Button';
import { useTheme, alpha } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import {
  TenantCostRow,
  CostFilterDto,
  CostBreakdownRow,
} from '../Model/CostType';
import {
  fmtCompact,
  fmtFull,
  CurrencyConfig,
  NOK,
} from '../../../Components/Layout/SqlServer/Common/util';
import ScopeBadge from './ScopeBadge';
import { exportTenantCsv, getTenantBreakdown } from '../Services/CostService';

interface TenantsTabProps {
  rows: TenantCostRow[];
  filter: CostFilterDto;
  currency?: CurrencyConfig;
  baseCurrency?: string;
}

const COLUMNS = [
  { label: 'Tenant', xs: 3 },
  { label: 'Total Cost', xs: 2 },
  { label: 'Distribution', xs: 3 },
  { label: 'Δ %', xs: 1 },
  { label: 'Scope', xs: 2 },
  { label: 'Farms', xs: 1 },
];

const TenantsTab: React.FC<TenantsTabProps> = ({
  rows,
  filter,
  currency = NOK,
  baseCurrency = 'NOK',
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tenantDetails, setTenantDetails] = useState<
    Record<string, CostBreakdownRow[]>
  >({});
  const [loadingTenants, setLoadingTenants] = useState<Set<string>>(new Set());

  const maxCost = Math.max(...rows.map((t) => t.TotalCost), 1);

  const handleExpand = async (tenantId: string) => {
    const isExpanding = expanded !== tenantId;
    setExpanded(isExpanding ? tenantId : null);

    // If expanding and we don't have data yet, fetch it
    if (
      isExpanding &&
      !tenantDetails[tenantId] &&
      !loadingTenants.has(tenantId)
    ) {
      setLoadingTenants((prev) => new Set(prev).add(tenantId));
      try {
        const details = await getTenantBreakdown(
          tenantId,
          filter.From,
          filter.To,
        );
        setTenantDetails((prev) => ({ ...prev, [tenantId]: details }));
      } catch (err) {
        console.error(`Failed to fetch details for ${tenantId}`, err);
      } finally {
        setLoadingTenants((prev) => {
          const next = new Set(prev);
          next.delete(tenantId);
          return next;
        });
      }
    }
  };

  const renderResourceBreakdown = (tenantId: string) => {
    if (loadingTenants.has(tenantId)) {
      return (
        <Box sx={{ py: 2 }}>
          <Skeleton variant="text" sx={{ width: '80%', mb: 1 }} />
          <Skeleton variant="text" sx={{ width: '60%', mb: 1 }} />
          <Skeleton variant="text" sx={{ width: '70%' }} />
        </Box>
      );
    }
    if (tenantDetails[tenantId]?.length > 0) {
      return tenantDetails[tenantId]
        .toSorted((a, b) => b.Cost - a.Cost)
        .map((r) => (
          <Box
            key={r.ResourceId ?? r.ResourceName}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.ResourceName}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: 10 }}
              >
                {r.ServiceType} • {r.Scope}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontWeight: 700, ml: 2 }}
            >
              {fmtFull(r.Cost, currency, baseCurrency)}
            </Typography>
          </Box>
        ));
    }
    return (
      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ py: 2, fontStyle: 'italic' }}
      >
        No detailed resource data available for this range.
      </Typography>
    );
  };

  const renderDeltaCell = (t: TenantCostRow, up: boolean) => {
    if (t.DeltaPercent === undefined || t.DeltaPercent === null) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {up ? (
          <TrendingUpIcon color="error" sx={{ fontSize: 16 }} />
        ) : (
          <TrendingDownIcon color="success" sx={{ fontSize: 16 }} />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600 }}
          color={up ? 'error.main' : 'success.main'}
        >
          {up ? '+' : ''}
          {t.DeltaPercent.toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  const renderFarmsCell = (
    t: TenantCostRow,
    isVirt: boolean,
    isExp: boolean,
  ) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {t.Farms.slice(0, 2).map((f) => (
          <Chip
            key={f}
            label={`f${f}`}
            size="small"
            sx={{ fontSize: 10, height: 20, bgcolor: 'action.hover' }}
          />
        ))}
      </Box>
      {!isVirt &&
        (isExp ? (
          <KeyboardArrowUpIcon color="action" />
        ) : (
          <KeyboardArrowDownIcon color="action" />
        ))}
    </Box>
  );

  const renderExpandedDetails = (t: TenantCostRow, isExp: boolean) => {
    if (t.IsVirtual) return null;
    return (
      <Collapse in={isExp}>
        <Grid2
          container
          spacing={3}
          sx={{ px: 3, py: 2, bgcolor: 'background.default' }}
        >
          <Grid2 size={{ xs: 3 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}
            >
              Regions
            </Typography>
            {t.Regions.length > 0 ? (
              t.Regions.map((r) => (
                <Typography key={r} variant="body2" color="text.secondary">
                  {r}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 3 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}
            >
              Prior Period Cost
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fmtFull(t.LastPeriodCost, currency, baseCurrency)}
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 3 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}
            >
              Allocated Shared
            </Typography>
            <Typography variant="body2" color="warning.main">
              {fmtFull(t.AllocatedSharedCost, currency, baseCurrency)}
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 3 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}
            >
              Environment
            </Typography>
            <Chip
              label={t.Environment || '—'}
              size="small"
              color={t.Environment === 'Prod' ? 'success' : 'warning'}
              variant="outlined"
            />
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                mt: 1,
                pt: 2,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontWeight: 800 }}
              >
                Resource-Level Breakup
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mb: 2,
                }}
              >
                {renderResourceBreakdown(t.Tenant)}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  sx={{
                    height: 32,
                    fontSize: 11,
                    fontWeight: 700,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    exportTenantCsv(t.Tenant, filter.From, filter.To);
                  }}
                >
                  Download Comprehensive CSV Report
                </Button>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Collapse>
    );
  };

  const renderTenantRow = (t: TenantCostRow) => {
    const pct = (t.TotalCost / maxCost) * 100;
    const isExp = expanded === t.Tenant;
    const isVirt = t.IsVirtual;
    const up = (t.DeltaPercent ?? 0) >= 0;
    const isProd = t.Environment === 'Prod';
    let dotBgColor: string;
    if (isVirt) {
      dotBgColor = 'text.disabled';
    } else if (isProd) {
      dotBgColor = 'success.main';
    } else {
      dotBgColor = 'primary.main';
    }
    let dotShadow: string;
    if (isVirt) {
      dotShadow = 'none';
    } else if (isProd) {
      dotShadow = `0 0 8px ${alpha(theme.palette.success.main, 0.4)}`;
    } else {
      dotShadow = `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`;
    }
    return (
      <Paper
        key={t.Tenant}
        elevation={isExp ? 2 : 1}
        sx={{
          overflow: 'hidden',
          cursor: isVirt ? 'default' : 'pointer',
          bgcolor: isVirt ? 'action.hover' : 'background.paper',
          borderRadius: 3,
          border: isExp
            ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            : '1px solid transparent',
          transition: 'all 0.2s ease',
        }}
        onClick={() => !isVirt && handleExpand(t.Tenant)}
      >
        <Grid2 container alignItems="center" sx={{ px: 2, py: 2 }}>
          <Grid2 size={{ xs: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: dotBgColor,
                  flexShrink: 0,
                  boxShadow: dotShadow,
                }}
              />
              <Box
                sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color={isVirt ? 'text.secondary' : 'text.primary'}
                    sx={{
                      fontWeight: 700,
                      fontStyle: isVirt ? 'italic' : 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.Tenant}
                  </Typography>
                  {!isVirt && t.Environment && (
                    <Chip
                      label={t.Environment}
                      size="small"
                      color={t.Environment === 'Prod' ? 'success' : 'warning'}
                      variant="outlined"
                      sx={{
                        height: 16,
                        fontSize: 8,
                        fontWeight: 700,
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>
                {!isVirt && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: 9 }}
                  >
                    {t.Farms.length > 0
                      ? `Farm: ${t.Farms[0]}`
                      : 'Platform Scope'}
                  </Typography>
                )}
              </Box>
              {(t.MergedShare ?? 0) > 0 && (
                <Chip
                  label={`+${fmtCompact(t.MergedShare ?? 0, currency)}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: 10, height: 20 }}
                />
              )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {fmtCompact(t.TotalCost, currency, baseCurrency)}
              </Typography>
              {!isVirt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 9, opacity: 0.7 }}
                >
                  vs {fmtCompact(t.LastPeriodCost, currency, baseCurrency)} last
                </Typography>
              )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 3 }} sx={{ pr: 3 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={isVirt ? 'inherit' : 'primary'}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'action.selected' }}
            />
          </Grid2>

          <Grid2 size={{ xs: 1 }}>{renderDeltaCell(t, up)}</Grid2>

          <Grid2 size={{ xs: 2 }}>
            <ScopeBadge scope={t.Scope} />
          </Grid2>

          <Grid2 size={{ xs: 1 }}>{renderFarmsCell(t, isVirt, isExp)}</Grid2>
        </Grid2>

        {renderExpandedDetails(t, isExp)}
      </Paper>
    );
  };

  return (
    <Box>
      <Grid2 container sx={{ px: 2, pb: 1.5 }}>
        {COLUMNS.map((c) => (
          <Grid2 size={{ xs: c.xs }} key={c.label}>
            <Typography variant="overline" color="text.secondary">
              {c.label}
            </Typography>
          </Grid2>
        ))}
      </Grid2>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rows.map((t) => renderTenantRow(t))}
      </Box>
    </Box>
  );
};

export default TenantsTab;
